import { GoogleMap, useJsApiLoader, Marker, type Libraries } from '@react-google-maps/api'
import { GOOGLE_MAPS_API_KEY } from '@/shared/constants'
import { mapPinIcon } from './mapPinIcon'

// Reuse the same loader id + libraries as the dashboard HeatMap so the script loads once.
const LIBRARIES: Libraries = ['places']

type Props = {
  lat?: number | string | null
  lng?: number | string | null
  /** Optional label shown when hovering the marker */
  title?: string
  height?: number
  zoom?: number
}

export function LocationMap({ lat, lng, title, height = 220, zoom = 15 }: Props) {
  const latitude = Number(lat)
  const longitude = Number(lng)
  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 0 && longitude === 0)

  const { isLoaded } = useJsApiLoader({
    id: 'drivys-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  })

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-xs text-gray-400" style={{ height }}>
        Map unavailable — missing Google Maps API key
      </div>
    )
  }

  if (!hasCoords) {
    return (
      <div className="rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-xs text-gray-400" style={{ height }}>
        No location set for this address
      </div>
    )
  }

  if (!isLoaded) {
    return <div className="rounded-lg bg-gray-100 animate-pulse" style={{ height }} />
  }

  const center = { lat: latitude, lng: longitude }

  return (
    <div className="rounded-lg overflow-hidden border border-gray-100">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height }}
        center={center}
        zoom={zoom}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
      >
        <Marker position={center} title={title} icon={mapPinIcon()} />
      </GoogleMap>
    </div>
  )
}
